import type React from "react";
import { useMemo, useState } from "react";
import { Pencil, Check, Minus, X, ChevronRight } from "lucide-react";
import { Button } from "@app/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@app/components/ui/tooltip";
import { MageXPatient } from "@prisma/client";
import { MageXPatientWithFeedbacks } from "@api/src/types/patient";
import API from "@app/services/api";
import useUser from "@app/zustand/user";
import { deepSimilarity, type DeepSimilarityInput } from "@app/utils/precision";
import { Trans, useLingui } from "@lingui/macro";
import { PatientSchema, TypeEnum } from "@api/src/types/few-schot-schema";
import { useCanValidate } from "@app/utils/use-can-validate";
import {
  buildFieldValidation,
  isSameFieldValue,
  resolveAggregatedValue,
} from "@app/utils/resolve-field-value";
import type { ServedGesture } from "@app/utils/resolve-field-value";
import type { FieldGesture } from "@api/src/types/patient";
import ValueEditModal from "./ValueEditModal";
import ValueDisplay from "./ValueDisplay";
import { formatValue } from "@app/utils/format-field-value";
import { labelledValue } from "@app/utils/value-labels";
import QuestionnaireMatrix from "./QuestionnaireMatrix";
import {
  correctedAnswersByDate,
  seriesForField,
  type Questionnaire,
} from "@app/utils/questionnaires";

interface ValueAndPropertyDisplayProps {
  loadPatient: () => void;
  label: string;
  property: keyof MageXPatient;
  type?: TypeEnum;
  frozen?: boolean;
  patient: MageXPatientWithFeedbacks;
  debug?: boolean;
  schema: PatientSchema;
  // Temporal projection page: render field values as bordered "petit encart"
  // cards (see ValueCard) — one card per array item — to uniformise scalar,
  // array and empty fields. Off everywhere else.
  cardVariant?: boolean;
  // Read-only viewer mode: blur only the field VALUE (the ontology field label
  // stays legible). Actions are already gated by ``canValidate``.
  // Date clinique (AAAA-MM-JJ) décrite par une validation faite ici — la position du
  // curseur temporel. Transmise à la modale et jointe au feedback. Absente hors page
  // temporelle : la validation décrit l'état courant.
  effectiveDate?: string | null;
  // Le document source qui établit l'acte dont parle une correction faite ici. La date dit à
  // partir de quand la valeur tient ; celui-ci dit de QUOI elle parle — et la frontière d'un
  // épisode est un intervalle, contre lequel une date seule ne se place pas. Absent hors des
  // moments d'un épisode : la portée est alors laissée à l'ancre, comme avant.
  scopeDocId?: string | null;
  // The gesture that governs this row, as the server serves it beside the value (``GET /state``,
  // ``GET /pathway``): its verdict, the value it judged, and whether the value shown is still that
  // one. This component neither elects it nor compares anything.
  gesture?: ServedGesture | null;
  // Le questionnaire dont ce champ est le total, quand il en est le total. Fourni par la page,
  // qui charge les questionnaires du dossier en une fois. Sa présence est ce qui fait apparaître
  // la flèche : un score lu dans un courrier n'a pas de sous-scores, et une flèche qui ouvre du
  // vide apprend en trois clics qu'elle ne sert à rien.
  questionnaire?: Questionnaire;
  // Has the dossier been read? Until it has, an empty field shows nothing rather than
  // « Non déterminé », and no gesture is offered.
  processed: boolean;
}

// Treats null / undefined / empty string / empty array as "no value".
// Used both to detect a missing aggregated value and a missing validated truth,
// so clearing a multiselect (which yields `[]`, not `null`) is handled the same
// way as clearing a text input.
const isEmptyValue = (v: unknown): boolean =>
  v === undefined ||
  v === null ||
  v === "" ||
  (Array.isArray(v) && v.length === 0);

const getEqualityPrecision = (
  value1: unknown,
  value2: unknown,
  type: ValueAndPropertyDisplayProps["type"],
  debug?: boolean,
): number => {
  if (value1 === value2) return 1;
  if (value1 === null || value2 === null) return 1;
  if (value1 === undefined || value2 === undefined) return 1;

  if (type === "number") {
    const value1Number = value1 as number;
    const value2Number = value2 as number;
    return Math.abs(value1Number - value2Number) < 0.0001 ? 1 : 0;
  }
  if (type === "date") {
    const value1Date = value1 as string;
    const value2Date = value2 as string;
    return value1Date === value2Date ? 1 : 0;
  }
  if (type === "boolean") {
    const value1Boolean = value1 as boolean;
    const value2Boolean = value2 as boolean;
    return value1Boolean === value2Boolean ? 1 : 0;
  }
  if (type === "string") {
    const value1String = value1 as string;
    const value2String = value2 as string;
    return value1String === value2String ? 1 : 0;
  }
  if (type === "array_string" || type === "array_enum" || type === "scores") {
    if (debug) console.warn("CHECKING DEEP SIMILARITY", { value1, value2 });
    return deepSimilarity(
      value1 as DeepSimilarityInput[],
      value2 as DeepSimilarityInput[],
    );
  }

  return 0;
};

export default function ValueAndPropertyDisplay({
  loadPatient,
  label,
  property,
  type = "string",
  frozen = false,
  patient,
  debug = false,
  schema,
  cardVariant = false,
  processed,
  effectiveDate = null,
  scopeDocId = null,
  gesture = null,
  questionnaire,
}: ValueAndPropertyDisplayProps) {
  useUser((state) => state.user!);
  const { t } = useLingui();
  const canValidate = useCanValidate();
  const [detailOpen, setDetailOpen] = useState(false);
  const hasDetail = Boolean(questionnaire && questionnaire.passations.length > 0);

  const hasBeenProcessed = processed;

  // The aggregated column can hold a scalar where the ontology declares an
  // array (same legacy retype as the feedback log). It must go through the same
  // coercion as ``correctedValue`` below, otherwise ``checkEquality`` compares a
  // list against a string and reports a disagreement on identical content —
  // lighting up the "corrigé" state on a field the clinician never contradicted.
  const storedValue = resolveAggregatedValue(patient, property, type);

  // Ce que la page tient pour la valeur extraite de ce champ.
  //
  // Dès qu'un questionnaire existe, c'est SA série qui fait foi, pas la colonne. La colonne de ce
  // dossier date ses relevés sur la consultation — trois Oswestry différents au même horodatage,
  // donc deux points invisibles sous le troisième — quand la passation est datée sur le formulaire
  // rempli. Elle porte aussi, pour deux champs, les valeurs d'un autre instrument.
  //
  // La substitution vaut pour la valeur ET pour les gestes, délibérément : un bouton « retenir la
  // valeur agrégée » qui enregistrerait la colonne pendant que l'écran affiche la série calculée
  // ferait valider d'un clic autre chose que ce qui est lu. C'est le défaut que la pastille verte
  // a déjà connu, et il ne se répare qu'en n'ayant qu'une valeur.
  const derivedValue = useMemo(
    () => (questionnaire ? seriesForField(questionnaire, property as string) : null),
    [questionnaire, property],
  );
  const aggregatedValue =
    derivedValue && derivedValue.length > 0 ? derivedValue : storedValue;
  // Pas de mention à l'écran quand les deux divergent. Elle a existé, et elle disait « la valeur
  // agrégée en compte 14 » : un diagnostic d'ingénierie posé sur un écran clinique, affiché sur
  // presque tous les scores tant que les colonnes ne sont pas rattrapées. L'écart se mesure en
  // base, il n'a pas à occuper le médecin.
  // The value a gesture just made here recorded, shown until ``loadPatient`` brings the projection
  // that carries it. The projection is the only other source: a correction is journaled, and the
  // value the server publishes is the value on screen.
  const [correctedValue, setCorrectedValue] = useState<unknown>(undefined);

  if (debug)
    console.warn("INITIAL LOG", {
      property,
      aggregatedValue,
      correctedValue,
    });

  // Le geste posé à l'instant, avant que ``loadPatient`` ne rapporte la charge utile à
  // jour. Sans lui, la pastille disparaîtrait le temps de l'aller-retour serveur.
  const [localGesture, setLocalGesture] = useState<FieldGesture | null>(null);

  // ── Le verdict affiché ────────────────────────────────────────────────────────────
  //
  // Une seule question décide de la pastille : QUEL GESTE un clinicien a-t-il posé sur ce
  // champ. Elle est tranchée par le serveur (``correction_stats.classify_gesture``), qui
  // est aussi ce que compte ``/correction/stats`` — donc l'écran et le laboratoire disent
  // le même nombre par construction.
  //
  // Ce n'était pas la question posée avant : la croix rouge comparait la colonne de vérité
  // à la colonne agrégée D'AUJOURD'HUI. Deux mécanismes la faisaient donc mentir, tous
  // deux mesurés en production :
  //
  //  * une ré-agrégation postérieure à la revue déplace la colonne agrégée sous des
  //    validations intactes — 5 croix sur un dossier revu à 100 %, le dossier ayant été
  //    ré-agrégé une minute après la fin de la revue ;
  //  * 956 colonnes de vérité portent une valeur qu'AUCUN geste n'explique (annotations
  //    entrées par un jeu de données, champs calculés, séries re-matérialisées) — 28
  //    croix sur un dossier où le médecin n'avait touché que 4 champs.
  //
  // La valeur affichée, elle, reste celle de la vérité : une correction survit à une
  // relecture, y compris quand elle dit « non déterminé ».
  const verdict: FieldGesture | null = localGesture ?? gesture?.verdict ?? null;

  // La valeur a bougé SOUS le geste.
  //
  // Une correction n'efface pas le passé du dossier : elle relève le plancher de son champ, et
  // un document postérieur s'empile par-dessus (``temporal_materialization``). Donc sur une
  // page datée, avancer le curseur au-delà d'un document plus récent affiche une valeur que
  // personne n'a jugée, sous une pastille qui dit le contraire.
  //
  // Trois réponses possibles, une seule tient. Faire disparaître la pastille effacerait un
  // geste qui a bien eu lieu et redonnerait le champ à relire à un médecin qui l'a déjà relu.
  // La laisser pleine ferait dire « validé » d'une valeur jamais vue. Reste la troisième : la
  // garder, et la DÉMARQUER — le geste est vrai, ce qu'il décrivait ne l'est plus.
  //
  // The server says so (``superseded``), comparing the value judged with the one it publishes; a
  // flag judged no value, so nothing supersedes it.
  const gestureSuperseded =
    gesture?.superseded === true && localGesture === null;

  // The value the gesture was about, named rather than explained: the tooltip cannot know why
  // the value moved, only that the gesture judged another one.
  const gestureLabel =
    formatValue(
      labelledValue(schema[property]?.value_labels, gesture?.value),
      property,
      type,
    ) || t`Non déterminé`;

  // `correctedValue` is the value a gesture made here recorded; it covers the "Confirmer l'absence
  // de valeur" case through the gesture itself, whose value is empty.
  const hasValidatedTruth =
    !isEmptyValue(correctedValue) || localGesture !== null;
  const hasAggregatedValue = !isEmptyValue(aggregatedValue);

  const equalityPrecision = !hasAggregatedValue
    ? undefined
    : getEqualityPrecision(correctedValue, aggregatedValue, type, debug);

  // Backwards-compat alias for the modal: "no truth set yet for this field".
  const isNotStatued = !hasValidatedTruth;

  if (debug)
    console.warn("INTERMEDIATE LOG", {
      correctedValue,
      aggregatedValue,
      hasAggregatedValue,
      hasValidatedTruth,
      verdict,
      equalityPrecision,
    });

  const [isModalOpen, setIsModalOpen] = useState(false);
  // Why the last check was not recorded. The page used to log it to the console and draw nothing, so
  // a refused check read as a button that did nothing.
  const [validationError, setValidationError] = useState<string | null>(null);

  async function givePositiveFeedback() {
    // Same builder as the record-wide "valider le reste" bar: one gesture, one body
    // shape, so the two paths can never write different things about the same field.
    //
    // It confirms what the row SHOWS. When a questionnaire supplies the series, that series is on
    // screen rather than the stored column, so it is the one validated — otherwise the check recorded
    // a value nobody was reading, and the badge it drew was dashed at once.
    const shown =
      derivedValue && derivedValue.length > 0
        ? ({
            ...patient,
            [property]: derivedValue,
          } as MageXPatientWithFeedbacks)
        : patient;
    const { value, body } = buildFieldValidation(shown, property, type);
    setValidationError(null);
    const response = await API.post({
      path: `/patient/${patient.id_magex}/feedback`,
      body: {
        ...body,
        is_correct: true as const,
        notes: null as string | null,
        // Jour clinique décrit par la validation (curseur temporel). null = état courant.
        effective_date: effectiveDate,
        scope_doc_id: scopeDocId,
      },
    });
    if (response.ok) {
      setCorrectedValue(value);
      setLocalGesture("validated");
      loadPatient();
    } else {
      const detail = (response as { detail?: unknown } | null)?.detail;
      setValidationError(
        typeof detail === "string" && detail.trim()
          ? detail
          : t`La validation n'a pas pu être enregistrée. Réessayez.`,
      );
    }
  }

  function correctValue() {
    setIsModalOpen(true);
  }

  const fieldSchema = schema[property];
  if (!fieldSchema) {
    if (debug) console.warn("No field schema", property);
  }

  return (
    <>
      <div className="@container min-w-0">
        <div className="flex flex-col gap-2 py-2 @sm:flex-row @sm:items-center @sm:justify-between @sm:gap-4">
          <span
            className={[
              "min-w-0 max-w-full text-sm text-slate-600 @sm:max-w-[200px] @sm:shrink-0",
              schema[property]?.description ? "cursor-help" : "",
            ].join(" ")}
            title={schema[property]?.description || ""} // Add tooltip with schema description
          >
            {/* La flèche précède le libellé plutôt que la valeur : elle ouvre CE champ, et la
                placer à droite l'aurait mise au milieu des boutons de validation, où elle aurait
                lu comme une troisième action sur la valeur. */}
            {hasDetail && (
              <button
                type="button"
                onClick={() => setDetailOpen((open) => !open)}
                aria-expanded={detailOpen}
                aria-label={
                  detailOpen
                    ? "Masquer le détail du questionnaire"
                    : "Afficher le détail du questionnaire"
                }
                className="mr-1 inline-flex h-5 w-5 items-center justify-center rounded align-text-bottom text-slate-400 hover:bg-slate-100 hover:text-primary"
              >
                <ChevronRight
                  className={`h-4 w-4 transition-transform ${detailOpen ? "rotate-90" : ""}`}
                />
              </button>
            )}
            <Trans>{label}</Trans>:
          </span>
          <div
            className="hidden h-px min-w-0 flex-1 rounded-full bg-slate-200 @sm:block"
            aria-hidden
          />
          <div className="flex min-w-0 w-full flex-row items-center justify-between gap-3 @sm:w-auto @sm:max-w-none @sm:flex-1 @sm:justify-end @sm:gap-4">
            <div
              className={[
                "flex min-w-0 flex-1 items-center @sm:space-x-6",
                // Cards need the full row width so items stack cleanly; the
                // inline variant stays content-sized and right-aligned.
                cardVariant ? "@sm:flex-1" : "@sm:flex-initial",
              ].join(" ")}
            >
              {!hasBeenProcessed && !hasValidatedTruth && !hasAggregatedValue ? (
                // No extraction has run yet on this row — showing "Non déterminé"
                // would imply the pipeline tried and failed. Render nothing
                // instead so the user understands the field simply hasn't been
                // processed.
                <span aria-hidden />
              ) : (
                <ValueDisplay
                  property={property}
                  schema={schema}
                  type={type}
                  debug={debug}
                  correctedValue={correctedValue as React.ComponentProps<typeof ValueDisplay>["correctedValue"]}
                  aggregatedValue={aggregatedValue as React.ComponentProps<typeof ValueDisplay>["aggregatedValue"]}
                  key={JSON.stringify(correctedValue)}
                  hasValidatedTruth={hasValidatedTruth}
                  isCorrected={verdict === "corrected"}
                  label={label}
                  cardVariant={cardVariant}
                />
              )}
            </div>
            <div className="flex shrink-0 flex-col items-center">
              {!frozen && (
                <TooltipProvider delayDuration={300}>
                  <div className="flex items-center gap-0.5 shrink-0">
                    {/* Pastille verte : le clinicien a confirmé la valeur extraite.
                        Elle survit à une ré-agrégation — si le modèle a changé d'avis
                        depuis, l'infobulle le dit, mais la validation reste une
                        validation.
                        ÉVIDÉE quand la valeur a bougé sous le geste : la validation reste
                        affichée parce qu'elle a bien eu lieu, mais elle cesse de se donner
                        pour une validation de ce qui est à l'écran. */}
                    {verdict === "validated" && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="flex h-8 w-8 items-center justify-center">
                            <span
                              className={
                                gestureSuperseded
                                  ? "flex h-6 w-6 items-center justify-center rounded-full border border-dashed border-green-400 bg-white"
                                  : "flex h-6 w-6 items-center justify-center rounded-full bg-green-100"
                              }
                            >
                              <Check
                                className={
                                  gestureSuperseded
                                    ? "h-3.5 w-3.5 text-green-600"
                                    : "h-3.5 w-3.5 text-green-700"
                                }
                              />
                            </span>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          {gestureSuperseded ? (
                            <Trans>
                              Un clinicien a validé « {gestureLabel} » pour ce champ, et ce
                              n'est pas la valeur affichée ici. La pastille décrit le geste,
                              pas la valeur ci-contre.
                            </Trans>
                          ) : (
                            <Trans>Valeur validée</Trans>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {/* Croix rouge : le clinicien a REMPLACÉ la valeur. C'est le geste
                        qui la déclenche, jamais une comparaison de colonnes — une
                        ré-agrégation postérieure ne peut donc plus en fabriquer, ni une
                        annotation entrée par un jeu de données. Cliquer rouvre la modale. */}
                    {verdict === "corrected" && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={correctValue}
                            className="flex h-8 w-8 items-center justify-center"
                            aria-label="Valeur corrigée par le clinicien"
                          >
                            <span
                              className={
                                gestureSuperseded
                                  ? "flex h-6 w-6 items-center justify-center rounded-full border border-dashed border-red-400 bg-white"
                                  : "flex h-6 w-6 items-center justify-center rounded-full bg-red-100"
                              }
                            >
                              <X
                                className={
                                  gestureSuperseded
                                    ? "h-3.5 w-3.5 text-red-600"
                                    : "h-3.5 w-3.5 text-red-700"
                                }
                              />
                            </span>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          {gestureSuperseded ? (
                            <Trans>
                              Un clinicien a corrigé ce champ en « {gestureLabel} », et ce n'est
                              pas la valeur affichée ici. Cliquez pour rouvrir.
                            </Trans>
                          ) : (
                            <Trans>
                              Valeur agrégée inexacte, corrigée par le clinicien
                            </Trans>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {/* Pastille grise : un désaccord dont aucune valeur n'est sortie.
                        Un TIRET et non une coche, parce que la coche dit « validé » partout
                        ailleurs dans l'interface et que ce geste dit l'inverse. Deux
                        populations la portent : les 1 135 lignes de la production écrites
                        par l'ancien crayon, qui envoyait ``is_correct: false`` même quand le
                        médecin enregistrait sans rien toucher, et les signalements explicites
                        — une raison donnée sans que la valeur soit corrigée. L'infobulle nomme
                        les deux plutôt que d'affirmer de l'une ce qui n'est vrai que de
                        l'autre. */}
                    {verdict === "flagged" && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="flex h-8 w-8 items-center justify-center">
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-100">
                              <Minus className="h-3.5 w-3.5 text-slate-600" />
                            </span>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <Trans>
                            Champ revu, valeur inchangée. Signalé comme inexact sans
                            correction proposée — ou, pour les gestes les plus anciens,
                            enregistré tel quel.
                          </Trans>
                        </TooltipContent>
                      </Tooltip>
                    )}

                    {/* Bouton « retenir » : aucun geste n'a été posé, et ce qui est
                        affiché EST la valeur agrégée.
                        La seconde condition n'est pas une précaution de style : le bouton
                        enregistre la colonne agrégée, alors que l'écran affiche la vérité
                        quand elle existe. Sur un champ portant une annotation entrée par
                        un jeu de données — 956 colonnes en production — le clinicien
                        aurait validé d'un clic autre chose que ce qu'il lisait. Là, seul
                        le crayon reste, et il montre les deux valeurs. */}
                    {canValidate &&
                      hasBeenProcessed &&
                      verdict === null &&
                      (isEmptyValue(correctedValue) ||
                        isSameFieldValue(correctedValue, aggregatedValue)) && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                            onClick={givePositiveFeedback}
                            aria-label={hasAggregatedValue ? "Retenir la valeur agrégée" : "Confirmer l'absence de valeur"}
                          >
                            <Check className="h-4 w-4 text-slate-600" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {hasAggregatedValue ? (
                            <Trans>Retenir la valeur agrégée</Trans>
                          ) : (
                            <Trans>Confirmer l'absence de valeur</Trans>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    )}

                    {canValidate && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0 border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                            onClick={correctValue}
                            aria-label="Définir la vraie valeur"
                          >
                            <Pencil className="h-4 w-4 text-slate-600" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <Trans>Définir la vraie valeur</Trans>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </TooltipProvider>
              )}
            </div>
          </div>
        </div>

        {validationError && (
          <p role="alert" className="mb-2 text-right text-xs text-red-600">
            {validationError}
          </p>
        )}

        {/* Le détail, déplié sous la ligne et non dans une infobulle : on le lit en le comparant
            au document, ce qu'un survol ne permet pas. Monté seulement à l'ouverture — le dossier
            de démonstration porte 14 passations d'Oswestry, donc 140 cellules par champ. */}
        {hasDetail && detailOpen && (
          <div className="mb-3 mt-1 rounded-lg border border-slate-200 bg-white">
            <div className="flex items-baseline gap-2 border-b border-slate-100 px-3 py-2">
              <span className="text-sm font-medium text-slate-700">
                {questionnaire!.label}
              </span>
              <span className="text-xs text-slate-500">
                {questionnaire!.passations.length}{" "}
                {questionnaire!.passations.length > 1 ? (
                  <Trans>passations</Trans>
                ) : (
                  <Trans>passation</Trans>
                )}
              </span>
            </div>
            <QuestionnaireMatrix
              questionnaire={questionnaire!}
              fieldKey={property as string}
              corrected={correctedAnswersByDate(correctedValue)}
            />
          </div>
        )}
      </div>

      <ValueEditModal
        open={isModalOpen}
        setIsOpen={setIsModalOpen}
        loadPatient={loadPatient}
        label={label}
        property={property}
        type={type}
        patient={patient}
        debug={debug}
        schema={schema}
        aggregatedValue={aggregatedValue as React.ComponentProps<typeof ValueEditModal>["aggregatedValue"]}
        correctedValue={correctedValue as React.ComponentProps<typeof ValueEditModal>["correctedValue"]}
        setCorrectedValue={setCorrectedValue as React.ComponentProps<typeof ValueEditModal>["setCorrectedValue"]}
        onGestureRecorded={setLocalGesture}
        isNotStatued={isNotStatued}
        calledFrom="ValueAndPropertyDisplay"
        effectiveDate={effectiveDate}
        scopeDocId={scopeDocId}
        questionnaire={questionnaire}
        // Until the reload after a gesture made here, the served one describes the field as it was.
        gesture={localGesture === null ? gesture : null}
      />
    </>
  );
}
